@let ordersItem = orders$ | async;
@let permissions = permissions$ | async;
@let meta = paginationMeta$ | async;
@let localItem = localOrders$ | async;
@let localRecords = (localRecords$ | async) ?? [];

<app-layout-toolbar
  title="Orders"
  [crumbs]="['Orders']"
  [showBackButton]="false">
  @if (permissions | hasPermission: PERMISSIONS.ORDERS.CREATE) {
    <a mat-flat-button color="primary" [routerLink]="orderPaths.Create">
      <mat-icon svgIcon="mat:add" class="mr-1"></mat-icon>
      Create Order
    </a>
  }
</app-layout-toolbar>

<div class="container p-6">
  <!-- Orders recorded on this device while offline (not yet on the server) -->
  @if (localItem && localItem.data.length > 0) {
    <div class="mb-6">
      <app-orders-table
        title="Local orders — waiting to sync"
        emptyMessage="No local orders."
        [orders]="localItem"
        [rows]="localItem.data.length"
        (reload)="syncNow()"
        (orderClick)="viewOrder($event.id)">
        <ng-container bulkActionTemplate>
          <button
            mat-stroked-button
            color="primary"
            class="max-sm:w-full"
            [disabled]="isOffline()"
            [matTooltip]="
              isOffline()
                ? 'Reconnect to sync'
                : 'Send these sales to the server now'
            "
            (click)="syncNow()">
            <mat-icon svgIcon="mat:cloud_upload" class="mr-1"></mat-icon>
            Sync now
          </button>
        </ng-container>
        <ng-template #orderActions let-o>
          <div class="flex items-center justify-end gap-1">
            <button
              appClickStopPropagation
              mat-icon-button
              (click)="viewOrder(o.id)"
              matTooltip="View details">
              <mat-icon svgIcon="mat:open_in_new"></mat-icon>
            </button>
            @if (qrPayable(localRecords, o)) {
              <button
                appClickStopPropagation
                mat-icon-button
                color="primary"
                (click)="showQr(localRecords, o)"
                matTooltip="Show QR / copy payment link">
                <mat-icon svgIcon="mat:qr_code_2"></mat-icon>
              </button>
            }
            @if (canCancelLocal(localRecords, o)) {
              <button
                appClickStopPropagation
                mat-icon-button
                class="text-red-500"
                (click)="cancelLocal(o)"
                matTooltip="Cancel (not yet synced)">
                <mat-icon svgIcon="mat:cancel"></mat-icon>
              </button>
            }
          </div>
        </ng-template>
      </app-orders-table>
    </div>
  }

  <!-- Filters -->
  <form
    [formGroup]="filterForm"
    class="card p-4 mb-6 flex flex-wrap items-center gap-3 lg:sticky lg:z-20 lg:top-[calc(var(--vex-toolbar-height)+var(--vex-navigation-height)+var(--vex-secondary-toolbar-height)+0.5rem)]">
    <mat-form-field class="flex-auto min-w-[200px]" subscriptSizing="dynamic">
      <mat-label>Search</mat-label>
      <mat-icon matPrefix svgIcon="mat:search" class="mr-2"></mat-icon>
      <input
        matInput
        formControlName="search"
        type="search"
        placeholder="Order #, customer name or phone…" />
      @if (filterForm.get("search")?.value) {
        <button
          matSuffix
          mat-icon-button
          type="button"
          aria-label="Clear"
          (click)="filterForm.get('search')?.setValue('')">
          <mat-icon svgIcon="mat:close"></mat-icon>
        </button>
      }
    </mat-form-field>

    <mat-form-field class="w-48 max-sm:w-full" subscriptSizing="dynamic">
      <mat-label>Status</mat-label>
      <mat-select formControlName="status">
        @for (opt of statusOptions; track $index) {
          <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field class="w-48 max-sm:w-full" subscriptSizing="dynamic">
      <mat-label>Source</mat-label>
      <mat-select formControlName="source">
        @for (opt of sourceOptions; track opt.value) {
          <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field class="w-44 max-sm:w-full" subscriptSizing="dynamic">
      <mat-label>From</mat-label>
      <input
        matInput
        [matDatepicker]="fromPicker"
        [max]="filterForm.get('to')?.value ?? today"
        formControlName="from" />
      <mat-datepicker-toggle matIconSuffix [for]="fromPicker" />
      <mat-datepicker #fromPicker />
    </mat-form-field>

    <mat-form-field class="w-44 max-sm:w-full" subscriptSizing="dynamic">
      <mat-label>To</mat-label>
      <input
        matInput
        [matDatepicker]="toPicker"
        [min]="filterForm.get('from')?.value"
        [max]="today"
        formControlName="to" />
      <mat-datepicker-toggle matIconSuffix [for]="toPicker" />
      <mat-datepicker #toPicker />
    </mat-form-field>

    <div class="flex-1 flex flex-wrap justify-end items-center gap-3 min-w-fit">
      <button
        mat-button
        type="button"
        class="shrink-0"
        [disabled]="!hasFilters"
        (click)="clearFilters()">
        <mat-icon svgIcon="mat:filter_alt_off" class="mr-1"></mat-icon>
        Clear
      </button>
    </div>
  </form>

  @if (isOffline()) {
    <p class="text-xs text-secondary mb-2 m-0" role="status">
      You're offline: server orders are shown as last loaded and can't be
      changed until you reconnect.
    </p>
  }

  @if (ordersItem) {
    <app-orders-table
      title="Orders"
      [orders]="ordersItem"
      [rows]="(paginationMeta$ | async)?.size ?? 25"
      (reload)="reload()"
      (orderClick)="onOrderClick($event)">
      <!-- Custom actions per row -->
      <ng-template #orderActions let-o>
        <div class="flex items-center justify-end gap-1">
          <button
            appClickStopPropagation
            mat-icon-button
            (click)="viewOrder(o.id)"
            matTooltip="View details">
            <mat-icon svgIcon="mat:open_in_new"></mat-icon>
          </button>
          @if (
            !isOffline() &&
            o.status !== "completed" &&
            o.status !== "cancelled" &&
            o.status !== "rejected"
          ) {
            @if (nextLabel(o.status)) {
              <button
                appClickStopPropagation
                mat-icon-button
                color="primary"
                (click)="select(o.id)"
                matTooltip="Requires action to {{ nextLabel(o.status) }}">
                <mat-icon svgIcon="mat:report"></mat-icon>
              </button>
            }
            <button
              appClickStopPropagation
              mat-icon-button
              class="text-red-500"
              (click)="cancel(o)"
              matTooltip="Cancel">
              <mat-icon svgIcon="mat:cancel"></mat-icon>
            </button>
          }
        </div>
      </ng-template>
    </app-orders-table>

    @if (meta && meta.totalPages > 1) {
      <app-paginator
        [pageLength]="meta.totalPages"
        [currentPage]="meta.page"
        (emitPage)="onPage($event)"></app-paginator>
    }
  }

  <!-- Side panel trigger (programmatic) -->
  <span
    class="hidden"
    [appSidePanelTrigger]="itemPopover"
    [useHostClick]="false"
    [preventClickPropagation]="true"
    (overlayDetachedChanged)="deselect()"></span>

  <!-- Detail panel -->
  <ng-template #itemPopover let-overlayRef="overlayRef" let-detach="detach">
    <app-shared-side-panel
      [overlayRef]="overlayRef"
      [detachOverlay]="detach"
      [showCloseButton]="false">
      @let sid = selectedId$ | async;
      @if (sid) {
        @if (ordersItem?.data; as list) {
          @if (selectedOrder(list, sid); as o) {
            <div class="flex items-center justify-between mb-4">
              <h3 class="title m-0">{{ o.orderNumber }}</h3>
              <button mat-icon-button (click)="detach(); deselect()">
                <mat-icon svgIcon="mat:close"></mat-icon>
              </button>
            </div>
            <div class="flex flex-col gap-3">
              <div>
                <p class="text-xs text-secondary font-medium">Customer</p>
                <p class="text-sm font-semibold text-default">
                  {{ o.checkoutName || "—" }}
                </p>
                @if (o.checkoutPhone) {
                  <p class="text-xs text-secondary">{{ o.checkoutPhone }}</p>
                }
              </div>
              <div>
                <p class="text-xs text-secondary font-medium">Fulfillment</p>
                <p class="text-sm text-default capitalize">
                  {{
                    o.fulfillmentMode
                      ? (o.fulfillmentMode | removeCharacter)
                      : "—"
                  }}
                </p>
                @if (o.checkoutAddress) {
                  <p class="text-xs text-secondary">
                    {{ o.checkoutAddress["streetAddress"] }},
                    {{ o.checkoutAddress["city"] }}
                  </p>
                }
              </div>
              <mat-divider></mat-divider>
              <div>
                <p class="text-xs text-secondary font-medium mb-2">Items</p>
                @for (line of o.lines; track $index) {
                  <div class="border border-divider rounded-lg p-3 mb-2">
                    <p class="text-sm font-medium text-default">
                      {{
                        line.productSnapshotName ||
                          line.variantSnapshotName ||
                          line.variantId
                      }}
                    </p>
                    <p class="text-xs text-secondary">
                      Qty: {{ line.quantity }} ×
                      {{ line.unitPriceMinor | amountToMajor | price }}
                    </p>
                    <p class="text-xs font-medium text-default mt-1">
                      {{ line.lineSubtotalMinor | amountToMajor | price }}
                    </p>
                  </div>
                }
              </div>
              <mat-divider></mat-divider>
              <div class="flex flex-col gap-1">
                <div class="flex justify-between text-sm">
                  <span class="text-secondary">Subtotal</span>
                  <span class="text-default">{{
                    o.subtotalMinor | amountToMajor | price
                  }}</span>
                </div>
                @if (o.taxTotalMinor) {
                  <div class="flex justify-between text-sm">
                    <span class="text-secondary">Tax</span>
                    <span class="text-default">{{
                      o.taxTotalMinor | amountToMajor | price
                    }}</span>
                  </div>
                }
                @if (o.lineDiscountTotalMinor || o.cartDiscountMinor) {
                  <div class="flex justify-between text-sm">
                    <span class="text-secondary">Discount</span>
                    <span class="text-default"
                      >-{{
                        (o.lineDiscountTotalMinor ?? 0) +
                          (o.cartDiscountMinor ?? 0)
                          | amountToMajor
                          | price
                      }}</span
                    >
                  </div>
                }
                <div class="flex justify-between text-sm font-semibold mt-1">
                  @let total = o.totalUserPaymentMinor ?? o.totalMinor;
                  <span>Total</span>
                  <span>{{ total | amountToMajor | price }}</span>
                </div>
              </div>
              @if (o.notesInternal) {
                <div class="bg-yellow-50 border border-yellow-200 rounded p-2">
                  <p class="text-xs text-yellow-700">{{ o.notesInternal }}</p>
                </div>
              }
              <div class="flex flex-col gap-2 mt-2">
                <button mat-stroked-button (click)="viewOrder(o.id)">
                  <mat-icon svgIcon="mat:open_in_new" class="mr-1"></mat-icon>
                  View details
                </button>
              </div>
              @if (
                !isOffline() &&
                o.status !== "completed" &&
                o.status !== "cancelled" &&
                o.status !== "rejected"
              ) {
                <div class="flex flex-col gap-2">
                  @if (nextLabel(o.status)) {
                    <button
                      mat-flat-button
                      color="primary"
                      (click)="advance(o)">
                      {{ nextLabel(o.status) }}
                    </button>
                  }
                  <button mat-button class="text-red-500" (click)="cancel(o)">
                    Cancel Order
                  </button>
                </div>
              }
            </div>
          }
        }
      }
    </app-shared-side-panel>
  </ng-template>
</div>
